import React, { forwardRef, useContext, useEffect, useId, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useAnimation } from "framer-motion";
import { useLightboxContext } from "../hooks/useLightboxContext";
import { InlineContainerContext } from "../context";
import { icons, colors2 } from "oolib";
import {
    StyledInlineChildPanelContent,
    StyledPanelShadow,
    StyledPanelOverlay,
    StyledBackButton,
    StyledLightboxTitle,
    LIGHTBOX_PANEL_WIDTH,
    LIGHTBOX_PANEL_MAX_OFFSET,
    INLINE_PANEL_TRANSITION,
} from "../styled";

const { ArrowLeft } = icons;
import type { LightboxContentProps } from "../types";

const DEFAULT_OFFSET_REM = LIGHTBOX_PANEL_WIDTH * LIGHTBOX_PANEL_MAX_OFFSET;

/**
 * Inline mode child renderer.
 * Portals into the root inline container as an overlapping flex sibling.
 *
 * Exit animation is imperative: triggerClose calls onStartExit() directly
 * on each panel — no React state update, no re-render cascade.
 * commitClose is called after animation by the shallowest exiting panel.
 */
export const InlineChildContent = forwardRef<HTMLDivElement, LightboxContentProps>(
    function InlineChildContent(
        {
            children,
            title,
            className,
            style,
            onEscapeKeyDown,
        },
        forwardedRef
    ) {
        const { open, setOpen, depth } = useLightboxContext();
        const inlineCtx = useContext(InlineContainerContext);
        const panelId = useId();
        const controls = useAnimation();
        const panelDivRef = useRef<HTMLDivElement>(null);
        const shadowRef = useRef<HTMLDivElement>(null);

        const closePanel = useCallback(() => setOpen(false), [setOpen]);

        // Stable ref to inlineCtx so onStartExit closure doesn't go stale
        const inlineCtxRef = useRef(inlineCtx);
        inlineCtxRef.current = inlineCtx;

        // Imperative exit callback — called by triggerClose, no React state
        const onStartExit = useCallback(() => {
            const offset = inlineCtxRef.current?.panelOffsetRef?.current ?? DEFAULT_OFFSET_REM;
            // Trigger shadow-out animation imperatively via DOM
            if (shadowRef.current) {
                shadowRef.current.setAttribute('data-exiting', 'true');
            }

            controls.start(
                { x: `${LIGHTBOX_PANEL_WIDTH - offset}rem` },
                INLINE_PANEL_TRANSITION
            ).then(() => {
                // Check at completion time — handles broader-close-during-animation
                const ctx = inlineCtxRef.current;
                if (ctx && ctx.exitingFromDepthRef.current === depth) {
                    ctx.commitClose();
                }
            });
        }, [controls, depth]);

        // ── Registration ──────────────────────────────────────────────
        useEffect(() => {
            const ctx = inlineCtxRef.current;
            if (!open || !ctx) return;
            ctx.registerPanel(panelId, depth, closePanel, onStartExit);
            return () => ctx.unregisterPanel(panelId);
        }, [open, panelId, depth, closePanel, onStartExit]);

        // ── Enter animation ─────────────────────────────────────────
        useEffect(() => {
            if (open) {
                controls.start({ x: 0 }, INLINE_PANEL_TRANSITION);
            }
        }, [open, controls]);

        // Delay overlay/back button appearance by 300ms after a deeper panel becomes visible
        const hasDeeperPanel = !!(inlineCtx && depth < inlineCtx.maxVisibleDepth);
        const [showOverlay, setShowOverlay] = useState(false);
        useEffect(() => {
            if (hasDeeperPanel) {
                const timer = setTimeout(() => setShowOverlay(true), 300);
                return () => clearTimeout(timer);
            }
            setShowOverlay(false);
        }, [hasDeeperPanel]);

        // ── Render decision ───────────────────────────────────────────
        if (!open || !inlineCtx?.containerRef.current) return null;

        const currentOffset = inlineCtx.panelOffsetRef?.current ?? DEFAULT_OFFSET_REM;

        return createPortal(
            <motion.div
                initial={{ x: `${LIGHTBOX_PANEL_WIDTH - currentOffset}rem` }}
                animate={controls}
                style={{
                    marginLeft: `-${LIGHTBOX_PANEL_WIDTH - currentOffset}rem`,
                    position: 'relative',
                    zIndex: depth * 10,
                    flexShrink: 0,
                    alignSelf: 'stretch',
                }}
            >
                <StyledPanelShadow ref={shadowRef} />
                <StyledInlineChildPanelContent
                    ref={(node: HTMLDivElement | null) => {
                        panelDivRef.current = node;
                        if (typeof forwardedRef === "function") {
                            forwardedRef(node);
                        } else if (forwardedRef) {
                            forwardedRef.current = node;
                        }
                    }}
                    $cascade
                    className={className}
                    style={{
                        ...style,
                        height: '100%',
                        '--panel-offset': `${currentOffset}rem`,
                    } as any}
                    data-lightbox-depth={depth}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Escape") {
                            e.stopPropagation();
                            onEscapeKeyDown?.(e.nativeEvent);
                            if (inlineCtx) {
                                inlineCtx.triggerClose(depth);
                            } else {
                                setOpen(false);
                            }
                        }
                    }}
                >
                    {title && <StyledLightboxTitle>{title}</StyledLightboxTitle>}
                    {children}
                    {showOverlay && inlineCtx && (
                        <div
                            style={{ position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none' }}
                        >
                            <StyledPanelOverlay onClick={() => inlineCtx.triggerClose(depth + 1)} />
                            <StyledBackButton onClick={() => inlineCtx.triggerClose(depth + 1)}>
                                <ArrowLeft size={20} color={colors2.grey80} weight="bold" />
                            </StyledBackButton>
                        </div>
                    )}
                </StyledInlineChildPanelContent>
            </motion.div>,
            inlineCtx.containerRef.current
        );
    }
);
